import { useState } from 'react';
import { LuMinus, LuPlus, LuShield } from 'react-icons/lu';

import { Account } from '@ndvr/portfolio-api';

import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/utils/cn';

import { clampLevel, LEVEL_STEP, LifestyleDraft, MAX_LEVEL, MIN_LEVEL } from './policy-lifestyle';

interface PolicyLifestyleProtectionCardProps {
    value: LifestyleDraft;
    accounts: Account[];
    onChange: (update: (current: LifestyleDraft) => LifestyleDraft) => void;
}

const SettingRow = ({
    title,
    description,
    children,
}: {
    title: string;
    description: string;
    children: React.ReactNode;
}) => (
    <div className="flex items-center gap-2.5 border-t border-[#ECE8E4] pt-3">
        <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-medium text-[#0D0D0D]">{title}</div>
            <p className="text-[11.5px] text-[#897E7B]">{description}</p>
        </div>
        {children}
    </div>
);

const stepButton =
    'flex h-9 w-9 items-center justify-center bg-white text-[#2C2424] hover:bg-[#F8F7F5] disabled:opacity-40';

/** Turns Lifestyle Protection on or off and sets how much of the upcoming withdrawals it holds in treasuries. */
export const PolicyLifestyleProtectionCard = ({ value, accounts, onChange }: PolicyLifestyleProtectionCardProps) => {
    const setEnabled = (enabled: boolean) =>
        onChange((current) => ({
            ...current,
            enabled,
            // Protection is pinned to one account; a newly enabled one starts on the first account.
            accountGUID: current.accountGUID ?? accounts[0]?.accountGUID,
        }));
    const setLevel = (level: number) => onChange((current) => ({ ...current, level: clampLevel(level) }));
    // What is being typed, while the input has focus. Only levels in range reach the draft, so typing "8" on the way
    // to "80" isn't bumped to 5, and closing the modal mid-edit can't leave a level the backend would reject.
    const [levelText, setLevelText] = useState<string>();

    return (
        <section
            aria-labelledby="lifestyle-protection-card-title"
            className="rounded-lg border border-[#CECAC6] bg-white p-3.5"
        >
            <div className="flex items-center gap-2.5">
                <div className="flex size-7 shrink-0 items-center justify-center rounded-[7px] bg-[#E9F5F6] text-[#2C656D]">
                    <LuShield aria-hidden="true" className="size-[17px]" />
                </div>
                <div className="min-w-0 flex-1">
                    <h3
                        id="lifestyle-protection-card-title"
                        className="text-cocoa-1000 text-[13.5px] leading-snug font-medium"
                    >
                        Lifestyle Protection
                    </h3>
                    <p className="text-[11.5px] leading-[1.45] text-[#897E7B]">
                        Hold upcoming withdrawals in short-term treasuries so they are secured from market swings.
                    </p>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={value.enabled}
                    aria-labelledby="lifestyle-protection-card-title"
                    disabled={accounts.length === 0}
                    onClick={() => setEnabled(!value.enabled)}
                    className={cn(
                        'shrink-0 rounded-[5px] px-2 py-1 text-xs font-semibold disabled:opacity-50',
                        value.enabled ? 'bg-[#2F6468] text-white' : 'bg-[#F2EFEC] text-[#5E5552]',
                    )}
                >
                    {value.enabled ? 'On' : 'Off'}
                </button>
            </div>
            {value.enabled && (
                <div className="mt-3 flex flex-col gap-3">
                    <SettingRow
                        title="Protection level"
                        description="Share of upcoming withdrawals held in short-term treasuries."
                    >
                        <div className="flex w-60 items-stretch divide-x divide-[#CECAC6] overflow-hidden rounded-lg border border-[#CECAC6] bg-white">
                            <button
                                type="button"
                                className={stepButton}
                                aria-label="Decrease protection level"
                                disabled={value.level <= MIN_LEVEL}
                                onClick={() => setLevel(value.level - LEVEL_STEP)}
                            >
                                <LuMinus aria-hidden="true" className="size-4" />
                            </button>
                            <div className="flex flex-1 items-center justify-center gap-0.5 px-2.5">
                                <Input
                                    type="number"
                                    aria-label="Protection level"
                                    min={MIN_LEVEL}
                                    max={MAX_LEVEL}
                                    value={levelText ?? value.level}
                                    onChange={(event) => {
                                        setLevelText(event.target.value);
                                        const level = Number.parseInt(event.target.value, 10);
                                        if (level >= MIN_LEVEL && level <= MAX_LEVEL) setLevel(level);
                                    }}
                                    onBlur={() => {
                                        const level = Number.parseInt(levelText ?? '', 10);
                                        if (Number.isFinite(level)) setLevel(level);
                                        setLevelText(undefined);
                                    }}
                                    className="h-9 w-[34px] border-0 bg-transparent p-0 text-right text-[15px] font-medium tabular-nums shadow-none focus-visible:ring-0"
                                />
                                <span className="text-sm text-[#897E7B]">%</span>
                            </div>
                            <button
                                type="button"
                                className={stepButton}
                                aria-label="Increase protection level"
                                disabled={value.level >= MAX_LEVEL}
                                onClick={() => setLevel(value.level + LEVEL_STEP)}
                            >
                                <LuPlus aria-hidden="true" className="size-4" />
                            </button>
                        </div>
                    </SettingRow>
                    <SettingRow
                        title="Hold protected funds in"
                        description="The account the short-term treasuries are held in."
                    >
                        <Select
                            value={value.accountGUID}
                            onValueChange={(accountGUID) => onChange((current) => ({ ...current, accountGUID }))}
                        >
                            <SelectTrigger
                                aria-label="Account holding protected funds"
                                className="h-[38px] w-60 rounded-lg border-[#CECAC6] bg-white text-[13px]"
                            >
                                <SelectValue placeholder="Select an account" />
                            </SelectTrigger>
                            <SelectContent>
                                {accounts.map((account) => (
                                    <SelectItem key={account.accountGUID} value={account.accountGUID}>
                                        {account.accountName.plainText}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </SettingRow>
                </div>
            )}
        </section>
    );
};
